import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import {
  WorkItemList,
  WorkItemStateEnum,
  WorkItemTypeEnum,
} from "../../../../store/types/work.types";
import {
  ACTION_RESULTS,
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
} from "../../../../utils/workItemUtils";

const FOLLOWUP_TYPES = new Set<WorkItemTypeEnum>(
  WORK_ITEM_CATEGORIES[WorkItemCategory.FOLLOWUP]
);

/** True for `FollowUp*` types — the ones a sales rep has agreed on with the
 *  customer (Mötesbokning), as opposed to internal planning (Planering). */
export const isBookedWithCustomer = (type: WorkItemTypeEnum): boolean =>
  FOLLOWUP_TYPES.has(type);

export const BOOKED_FG = "#6B4FB8";
export const BOOKED_BG = "#EDE8F8";

export const CONTACT_ATTEMPT_FG = "#9B6B00";
export const CONTACT_ATTEMPT_BG = "#FFF1CF";

export const isContactAttempt = (type: WorkItemTypeEnum): boolean =>
  type === WorkItemTypeEnum.ContactAttempt;

/** En render-sektion: antingen ett vanligt item, eller en grupp av
 *  konsekutiva kontaktförsök som ska renderas hopstaplat/minimalt. */
export type RenderSegment =
  | { kind: "item"; item: WorkItemList }
  | { kind: "attempts"; items: WorkItemList[] };

/** Slår ihop konsekutiva kontaktförsök till en grupp, övriga items lämnas
 *  som egna sektioner. Bevarar ordningen i listan. */
export const groupContactAttempts = (
  items: WorkItemList[]
): RenderSegment[] => {
  const segments: RenderSegment[] = [];
  let run: WorkItemList[] = [];
  const flush = () => {
    if (run.length > 0) {
      segments.push({ kind: "attempts", items: run });
      run = [];
    }
  };
  for (const item of items) {
    if (isContactAttempt(item.Type)) {
      run.push(item);
    } else {
      flush();
      segments.push({ kind: "item", item });
    }
  }
  flush();
  return segments;
};

export type ChannelKind =
  | "phone"
  | "sms"
  | "meet"
  | "email"
  | "admin"
  | "other";

export const getChannelKind = (type: WorkItemTypeEnum): ChannelKind => {
  switch (type) {
    case WorkItemTypeEnum.PhoneCall:
    case WorkItemTypeEnum.WelcomeCall:
    case WorkItemTypeEnum.FollowUpPhoneCall:
    case WorkItemTypeEnum.OpportunityCall:
    case WorkItemTypeEnum.ReconnectToCustomer:
      return "phone";
    case WorkItemTypeEnum.Sms:
    case WorkItemTypeEnum.FollowUpSms:
      return "sms";
    case WorkItemTypeEnum.PhysicalMeeting:
    case WorkItemTypeEnum.FollowUpPhysicalMeeting:
    case WorkItemTypeEnum.DigitalMeeting:
    case WorkItemTypeEnum.FollowUpDigitalMeeting:
      return "meet";
    case WorkItemTypeEnum.Email:
    case WorkItemTypeEnum.FollowUpEmail:
    case WorkItemTypeEnum.HandleEmail:
      return "email";
    case WorkItemTypeEnum.Admin:
      return "admin";
    default:
      return "other";
  }
};

export const isFutureItem = (item: WorkItemList): boolean => {
  if (!item.ScheduledTo) return false;
  return new Date(item.ScheduledTo).getTime() > Date.now();
};

export const isOverdueItem = (item: WorkItemList): boolean => {
  if (!item.ScheduledTo) return false;
  if (item.State !== 1) return false;
  return new Date(item.ScheduledTo).getTime() < Date.now();
};

export type DateBucket =
  | "overdue"
  | "today"
  | "tomorrow"
  | "thisWeek"
  | "later"
  | "none";

export const DATE_BUCKET_ORDER: DateBucket[] = [
  "overdue",
  "today",
  "tomorrow",
  "thisWeek",
  "later",
  "none",
];

export const DATE_BUCKET_LABEL: Record<DateBucket, string> = {
  overdue: "Försenad",
  today: "Idag",
  tomorrow: "Imorgon",
  thisWeek: "Den här veckan",
  later: "Senare",
  none: "Utan datum",
};

export const getDateBucket = (iso: string | null | undefined): DateBucket => {
  if (!iso) return "none";
  let d: Date;
  try {
    d = parseISO(iso);
  } catch {
    return "none";
  }
  const now = new Date();
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const startDayAfterTomorrow = new Date(startTomorrow);
  startDayAfterTomorrow.setDate(startDayAfterTomorrow.getDate() + 1);
  const startNextWeek = new Date(startToday);
  startNextWeek.setDate(startNextWeek.getDate() + 7);

  if (d.getTime() < now.getTime()) return "overdue";
  if (d < startTomorrow) return "today";
  if (d < startDayAfterTomorrow) return "tomorrow";
  if (d < startNextWeek) return "thisWeek";
  return "later";
};

export const KIND_FG: Record<ChannelKind, string> = {
  phone: "#B2433A",
  sms: "#1F7A75",
  meet: "#6B4FB8",
  email: "#2563A6",
  admin: "#6B655F",
  other: "#FF5F00",
};

export const KIND_BG: Record<ChannelKind, string> = {
  phone: "#FBE9E7",
  sms: "#E1F1F0",
  meet: "#EDE8F8",
  email: "#E6EEF7",
  admin: "#ECE7E0",
  other: "#FFF3E8",
};

export const ORANGE = "#FF5F00";
export const ORANGE_50 = "#FFF3E8";
export const ORANGE_100 = "#FFE3CC";
export const GREEN = "#2F7D54";
export const GREEN_BG = "#E6F2EC";
export const INK_4 = "#9B948D";
export const INK_3 = "#6B655F";
export const INK = "#181614";
export const LINE = "#ECE7E0";
export const LINE_2 = "#E2DCD3";
export const PAPER = "#FFFFFF";
export const BG_SURFACE = "#F6F4F0";
export const ROSE = "#B2433A";
export const ROSE_BG = "#FBE9E7";
export const ROSE_LINE = "#F3C9C3";
export const AMBER = "#9B6B00";
export const AMBER_BG = "#FFF1CF";
/** Muted surface/border for avslutade kort. */
export const DONE_BG = "#FBFAF8";
export const DONE_LINE = "#F1EDE7";
export const LANE_BODY_BG = "#FCFBF9";

/** Rullad status för ett ärende, härledd ur dess aktiviteter. */
export interface CaseRollup {
  total: number;
  done: number;
  overdue: number;
  noDate: number;
  fraction: number;
}

export const getCaseRollup = (children: WorkItemList[]): CaseRollup => {
  const total = children.length;
  const done = children.filter(
    (c) => c.State === WorkItemStateEnum.Closed
  ).length;
  const overdue = children.filter(isOverdueItem).length;
  const noDate = children.filter(
    (c) => c.State === WorkItemStateEnum.Active && !c.ScheduledTo
  ).length;
  return {
    total,
    done,
    overdue,
    noDate,
    fraction: total > 0 ? done / total : 0,
  };
};

const isCurrentYear = (d: Date) => d.getFullYear() === new Date().getFullYear();

/** "3 maj" if same year, "3 maj 25" otherwise. */
export const formatDateShort = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    const d = parseISO(iso);
    const fmt = isCurrentYear(d) ? "d MMM" : "d MMM yy";
    return format(d, fmt, { locale: sv });
  } catch {
    return iso ?? "—";
  }
};

/** "3 maj · 14:00" or "3 maj 25 · 14:00" depending on year. */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    const d = parseISO(iso);
    const fmt = isCurrentYear(d) ? "d MMM" : "d MMM yy";
    return `${format(d, fmt, { locale: sv })} · ${format(d, "HH:mm")}`;
  } catch {
    return iso ?? "—";
  }
};

export type ResultTone = "success" | "warning" | "skipped" | "neutral";

export const getResultTone = (raw: string | null): ResultTone => {
  if (!raw) return "neutral";
  if (raw === ACTION_RESULTS.COMPLETED || raw === "Genomförd") return "success";
  if (raw === ACTION_RESULTS.SKIPPED || raw === "Skippad") return "skipped";
  if (
    raw === ACTION_RESULTS.CUSTOMER_NOT_INTERESTED ||
    raw === "Kund inte intresserad"
  )
    return "warning";
  return "neutral";
};

export const RESULT_TONE_BG: Record<ResultTone, string> = {
  success: "#E6F2EC",
  warning: "#FBE9E7",
  skipped: "#ECE7E0",
  neutral: "#F0EBE3",
};

export const RESULT_TONE_FG: Record<ResultTone, string> = {
  success: "#2F7D54",
  warning: "#B2433A",
  skipped: "#9B948D",
  neutral: "#3F3A36",
};
